import React, { useState, useRef, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Icon from './AppIcon';
import useAuctionNotifications from '../hooks/useAuctionNotifications';

const NOTIFICATION_ICONS = {
  outbid: { icon: 'TrendingUp', color: 'text-orange-500', bg: 'bg-orange-500/10' },
  auction_won: { icon: 'Trophy', color: 'text-yellow-500', bg: 'bg-yellow-500/10' },
  bid_placed: { icon: 'Gavel', color: 'text-primary', bg: 'bg-primary/10' },
  auction_ended: { icon: 'Clock', color: 'text-muted-foreground', bg: 'bg-muted/20' },
  default: { icon: 'Bell', color: 'text-primary', bg: 'bg-primary/10' }
};

const formatTime = (dateStr) => {
  const diff = Date.now() - new Date(dateStr)?.getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return 'just now';
  if (mins < 60) return `${mins}m ago`;
  const hrs = Math.floor(mins / 60);
  if (hrs < 24) return `${hrs}h ago`;
  return `${Math.floor(hrs / 24)}d ago`;
};

const NotificationBell = ({ className = '' }) => {
  const [isOpen, setIsOpen] = useState(false);
  const { notifications, unreadCount, markAsRead, markAllAsRead } = useAuctionNotifications();
  const dropdownRef = useRef(null);
  const navigate = useNavigate();

  // Close on outside click
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (dropdownRef?.current && !dropdownRef?.current?.contains(e?.target)) {
        setIsOpen(false);
      }
    };
    if (isOpen) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  const handleNotificationClick = async (notification) => {
    if (!notification?.is_read) {
      await markAsRead(notification?.id);
    }
    if (notification?.auction_id) {
      navigate('/marketplace');
    }
    setIsOpen(false);
  };

  const handleMarkAllRead = async (e) => {
    e?.stopPropagation();
    await markAllAsRead();
  };

  const iconConfig = (type) => NOTIFICATION_ICONS?.[type] || NOTIFICATION_ICONS?.default;

  return (
    <div className={`relative ${className}`} ref={dropdownRef}>
      {/* Bell Button */}
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2.5 text-muted-foreground hover:text-foreground transition-colors duration-200 premium-hover rounded-lg min-w-[44px] min-h-[44px] flex items-center justify-center"
        aria-label={`Notifications${unreadCount > 0 ? ` (${unreadCount} unread)` : ''}`}
      >
        <Icon name="Bell" size={20} />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 min-w-[18px] h-[18px] bg-conversion-accent text-white text-[10px] font-bold rounded-full flex items-center justify-center px-1 border-2 border-background">
            {unreadCount > 99 ? '99+' : unreadCount}
          </span>
        )}
      </button>
      {/* Dropdown */}
      {isOpen && (
        <div className="absolute right-0 mt-2 w-80 sm:w-96 bg-popover border border-border rounded-lg shadow-premium-lg animate-scale-in z-50 overflow-hidden">
          {/* Header */}
          <div className="flex items-center justify-between px-4 py-3 border-b border-border">
            <div className="flex items-center space-x-2">
              <Icon name="Bell" size={16} className="text-primary" />
              <h3 className="text-sm font-semibold text-foreground">Notifications</h3>
              {unreadCount > 0 && (
                <span className="px-1.5 py-0.5 bg-primary/10 text-primary text-xs font-medium rounded-full">
                  {unreadCount} new
                </span>
              )}
            </div>
            {unreadCount > 0 && (
              <button
                onClick={handleMarkAllRead}
                className="text-xs text-primary hover:text-primary/80 transition-colors font-medium"
              >
                Mark all read
              </button>
            )}
          </div>

          {/* Notification List */}
          <div className="max-h-80 overflow-y-auto">
            {notifications?.length === 0 ? (
              <div className="flex flex-col items-center justify-center py-10 px-4 text-center">
                <div className="w-12 h-12 bg-muted/30 rounded-full flex items-center justify-center mb-3">
                  <Icon name="BellOff" size={20} className="text-muted-foreground" />
                </div>
                <p className="text-sm text-muted-foreground">No notifications yet</p>
                <p className="text-xs text-muted-foreground/70 mt-1">You'll be notified when you're outbid or win an auction</p>
              </div>
            ) : (
              notifications?.map((notification) => {
                const config = iconConfig(notification?.notification_type);
                return (
                  <button
                    key={notification?.id}
                    onClick={() => handleNotificationClick(notification)}
                    className={`w-full flex items-start space-x-3 px-4 py-3 hover:bg-muted/30 transition-colors text-left border-b border-border/50 last:border-0 ${
                      !notification?.is_read ? 'bg-primary/5' : ''
                    }`}
                  >
                    <div className={`flex-shrink-0 w-9 h-9 rounded-full flex items-center justify-center mt-0.5 ${config?.bg}`}>
                      <Icon name={config?.icon} size={16} className={config?.color} />
                    </div>
                    <div className="flex-1 min-w-0">
                      <div className="flex items-start justify-between gap-2">
                        <p className={`text-sm font-medium leading-tight ${!notification?.is_read ? 'text-foreground' : 'text-muted-foreground'}`}>
                          {notification?.title}
                        </p>
                        {!notification?.is_read && (
                          <span className="flex-shrink-0 w-2 h-2 bg-primary rounded-full mt-1.5" />
                        )}
                      </div>
                      <p className="text-xs text-muted-foreground mt-0.5 line-clamp-2">{notification?.message}</p>
                      <p className="text-xs text-muted-foreground/60 mt-1">{formatTime(notification?.created_at)}</p>
                    </div>
                  </button>
                );
              })
            )}
          </div>

          {/* Footer */}
          {notifications?.length > 0 && (
            <div className="px-4 py-2.5 border-t border-border">
              <button
                onClick={() => { navigate('/marketplace'); setIsOpen(false); }}
                className="text-xs text-primary hover:text-primary/80 transition-colors font-medium w-full text-center"
              >
                View all auctions →
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default NotificationBell;
